import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Copy, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;
const WS_URL = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws`;

const REACT_NATIVE_CODE = `// =================================================================
// 📱 Rs. Mart / Rani Super Market — React Native API Client
// Drop this file into your Expo / React Native App (e.g. src/services/api.js)
// Both Website & Android App now read/write the EXACT SAME database!
// =================================================================

const BASE_URL = "${API_BASE}";
const WS_URL = "${WS_URL}";

export const RSmartAPI = {
  // 1. App Boot Config & Categories
  getConfig: async () => {
    const res = await fetch(\`\${BASE_URL}/mobile/config\`);
    return res.json();
  },

  // 2. Fetch Live Products Catalog (Same for Web & App)
  getProducts: async (categoryId = null, search = "") => {
    const params = new URLSearchParams();
    if (categoryId) params.append("category_id", categoryId);
    if (search) params.append("q", search);
    params.append("limit", "100");
    const res = await fetch(\`\${BASE_URL}/products?\${params.toString()}\`);
    return res.json();
  },

  // 3. Get Single Product with Real-time Stock
  getProductDetail: async (productId) => {
    const res = await fetch(\`\${BASE_URL}/products/\${productId}\`);
    return res.json();
  },

  // 4. Server-Side Price & Stock Validation Quote
  getCartQuote: async (items, couponCode = null) => {
    const res = await fetch(\`\${BASE_URL}/checkout/quote\`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: items.map(i => ({ product_id: i.id, quantity: i.quantity })),
        coupon_code: couponCode,
        payment_method: "cod",
        idempotency_key: "quote_" + Date.now()
      })
    });
    return res.json();
  },

  // 5. Checkout / Place Order (Atomic Stock Deduction)
  placeOrder: async (token, items, address, paymentMethod = "cod") => {
    const idempotencyKey = "app_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
    const res = await fetch(\`\${BASE_URL}/checkout\`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": \`Bearer \${token}\`
      },
      body: JSON.stringify({
        items: items.map(i => ({ product_id: i.id, quantity: i.quantity })),
        address: address,
        payment_method: paymentMethod,
        idempotency_key: idempotencyKey,
        origin_url: "rsmart://app"
      })
    });
    return res.json();
  },

  // 6. Customer Login
  login: async (email, password) => {
    const res = await fetch(\`\${BASE_URL}/auth/login\`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password })
    });
    return res.json();
  },

  // 7. Live Real-time WebSocket connection
  subscribeRealtime: (onMessage) => {
    const ws = new WebSocket(WS_URL);
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        onMessage(data); // e.g. stock.changed, order.created, order.updated
      } catch (err) {}
    };
    return () => ws.close();
  }
};
`;

function EndpointRow({ method, path, desc }) {
  const methodColors = {
    GET: "bg-sky-100 text-sky-800",
    POST: "bg-emerald-100 text-emerald-800",
    PUT: "bg-amber-100 text-amber-800",
    DELETE: "bg-rose-100 text-rose-800",
    WS: "bg-purple-100 text-purple-800",
  };
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100 gap-2">
      <div className="flex items-center gap-2">
        <span className={`text-[11px] font-extrabold px-2 py-0.5 rounded ${methodColors[method] || "bg-slate-200 text-slate-700"}`}>{method}</span>
        <code className="text-xs font-bold text-slate-800">{path}</code>
      </div>
      <p className="text-xs text-slate-500">{desc}</p>
    </div>
  );
}

export function IntegrationTabs() {
  const [activeTab, setActiveTab] = useState("overview");
  const [copied, setCopied] = useState(false);

  const copyCode = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        <button
          data-testid="tab-api-snippet"
          onClick={() => setActiveTab("code")}
          className={`px-4 py-2 rounded-lg font-bold text-sm ${activeTab === "code" ? "bg-emerald-600 text-white" : "bg-white border text-slate-700"}`}
        >
          📱 Android App Integration Code (Copy & Paste)
        </button>
        <button
          data-testid="tab-endpoints"
          onClick={() => setActiveTab("endpoints")}
          className={`px-4 py-2 rounded-lg font-bold text-sm ${activeTab === "endpoints" ? "bg-emerald-600 text-white" : "bg-white border text-slate-700"}`}
        >
          🌐 Unified API Endpoints Reference
        </button>
      </div>

      {activeTab === "code" ? (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="font-bold text-base text-slate-900">React Native / Android App Client Code</h2>
              <p className="text-xs text-slate-500">
                Apne React Native / Expo project mein ek file banayein (e.g. <code className="bg-slate-100 px-1 py-0.5 rounded text-emerald-700 font-mono">src/api/rsmartApi.js</code>) aur neeche ka code paste karein.
              </p>
            </div>
            <button
              data-testid="copy-code-btn"
              onClick={() => copyCode(REACT_NATIVE_CODE)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs flex items-center gap-1.5 self-start"
            >
              {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />} {copied ? "Copied!" : "Copy Code"}
            </button>
          </div>

          <div className="bg-slate-900 text-slate-200 rounded-xl p-4 overflow-x-auto text-xs font-mono max-h-[420px]">
            <pre>{REACT_NATIVE_CODE}</pre>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-emerald-900 text-xs space-y-2">
            <p className="font-bold text-sm flex items-center gap-1.5">
              <ShieldCheck size={16} /> How the App & Website stay 100% Synced:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Products & Pricing:</strong> Jab aap Admin mein kisi product ka price ya photo change karte hain, wo Website aur Android App dono pe TURANT dikhta hai.</li>
              <li><strong>Stock & Inventory:</strong> Jab Website ya App pe koi order karta hai ya POS counter par sale hoti hai, central stock automatically deduct ho jata hai — double selling nahi hogi.</li>
              <li><strong>Orders:</strong> App ke orders aur Website ke orders dono Admin Panel mein <code className="bg-emerald-100 px-1 rounded">/admin/orders</code> par real-time aate hain.</li>
              <li><strong>Customer Accounts:</strong> Customer App mein register kare ya Website par, same email & password se dono jagah login ho sakta hai.</li>
            </ul>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h2 className="font-bold text-base text-slate-900">Unified API Base URL</h2>
          <div className="flex items-center gap-2 bg-slate-100 rounded-xl p-3">
            <code className="text-sm font-bold text-emerald-700 flex-1 truncate">{API_BASE}</code>
            <button
              data-testid="copy-base-url-btn"
              onClick={() => copyCode(API_BASE)}
              className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1"
            >
              <Copy size={12} /> Copy URL
            </button>
          </div>

          <div className="space-y-3 pt-2">
            <EndpointRow method="GET" path="/api/mobile/config" desc="App boot config: store info, festive banners, categories & feature flags" />
            <EndpointRow method="GET" path="/api/products" desc="Live product catalog with real-time stock and prices" />
            <EndpointRow method="GET" path="/api/products/{id}" desc="Single product detail with stock and GST rate" />
            <EndpointRow method="POST" path="/api/checkout/quote" desc="Server-side cart pricing with GST, delivery & coupon calculation" />
            <EndpointRow method="POST" path="/api/checkout" desc="Place order (atomic stock deduction + idempotency protection)" />
            <EndpointRow method="GET" path="/api/orders" desc="Customer's order history & live delivery timeline" />
            <EndpointRow method="POST" path="/api/pos/sale" desc="POS Counter quick sale (immediate stock reduction)" />
            <EndpointRow method="POST" path="/api/auth/login" desc="Customer login with email/password (returns JWT token)" />
            <EndpointRow method="WS" path="/api/ws" desc="WebSocket real-time push events for stock changes and order updates" />
          </div>
        </div>
      )}
    </>
  );
}
